import React from "react";

/**
 * Tile motif primitives. Every tile is a flat rounded rectangle with a
 * faint jade double-edge that echoes the logo, and content in the
 * brand palette only. No emoji, no stock art.
 */

export const TileFrame = ({
  width = 88,
  height = 120,
  children,
  variant = "cream",
  className = "",
  ariaLabel,
}) => {
  const bg = variant === "cream" ? "#F5F0E8" : "#FFFFFF";
  return (
    <svg
      viewBox="0 0 88 120"
      width={width}
      height={height}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel || "Mahjong tile"}
      className={className}
    >
      <rect x="2" y="4" width="84" height="114" rx="10" fill="#2E5C44" />
      <rect x="3" y="2" width="82" height="114" rx="10" fill={bg} />
      <rect
        x="3"
        y="2"
        width="82"
        height="114"
        rx="10"
        fill="none"
        stroke="#3D7A5A"
        strokeWidth="1.5"
      />
      {children}
    </svg>
  );
};

// 中 red-dragon tile — the hero anchor.
export const TileZhong = ({ width = 120, height = 164, className = "" }) => (
  <TileFrame
    width={width}
    height={height}
    className={className}
    ariaLabel="Zhong red dragon tile"
  >
    <text
      x="44"
      y="78"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="Noto Serif SC, serif"
      fontWeight="900"
      fontSize="60"
      fill="#C0392B"
    >
      中
    </text>
  </TileFrame>
);

// Bamboo (索) tile — 3 simplified stems
export const TileBamboo = ({ width = 88, height = 120, className = "" }) => (
  <TileFrame width={width} height={height} className={className} ariaLabel="Bamboo tile">
    {[22, 44, 66].map((x) => (
      <g key={x}>
        <rect x={x - 4} y="30" width="8" height="60" rx="4" fill="#3D7A5A" />
        <rect x={x - 1.5} y="30" width="3" height="60" fill="#2E5C44" opacity="0.35" />
      </g>
    ))}
    <circle cx="22" cy="30" r="3" fill="#3D7A5A" />
    <circle cx="44" cy="30" r="3" fill="#3D7A5A" />
    <circle cx="66" cy="30" r="3" fill="#3D7A5A" />
    <circle cx="22" cy="90" r="3" fill="#3D7A5A" />
    <circle cx="44" cy="90" r="3" fill="#3D7A5A" />
    <circle cx="66" cy="90" r="3" fill="#3D7A5A" />
  </TileFrame>
);

// Dots (筒) tile — 3 concentric dots
export const TileDots = ({ width = 88, height = 120, className = "" }) => (
  <TileFrame width={width} height={height} className={className} ariaLabel="Dots tile">
    {[
      [44, 32],
      [44, 60],
      [44, 88],
    ].map(([cx, cy]) => (
      <g key={`${cx}-${cy}`}>
        <circle cx={cx} cy={cy} r="13" fill="#3D7A5A" />
        <circle cx={cx} cy={cy} r="9" fill="#F5F0E8" />
        <circle cx={cx} cy={cy} r="5" fill="#C0392B" />
      </g>
    ))}
  </TileFrame>
);

// Character (萬) tile — uses 萬 glyph
export const TileWan = ({ width = 88, height = 120, className = "" }) => (
  <TileFrame width={width} height={height} className={className} ariaLabel="Character tile">
    <text
      x="44"
      y="40"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="Noto Serif SC, serif"
      fontWeight="900"
      fontSize="26"
      fill="#1A1A1A"
    >
      九
    </text>
    <text
      x="44"
      y="82"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="Noto Serif SC, serif"
      fontWeight="900"
      fontSize="34"
      fill="#C0392B"
    >
      萬
    </text>
  </TileFrame>
);

// East wind 東 tile
export const TileEast = ({ width = 88, height = 120, className = "" }) => (
  <TileFrame width={width} height={height} className={className} ariaLabel="East wind tile">
    <text
      x="44"
      y="62"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="Noto Serif SC, serif"
      fontWeight="900"
      fontSize="52"
      fill="#1A1A1A"
    >
      東
    </text>
  </TileFrame>
);

// Flat icon variants derived from the tile motif — used in feature cards / steps.
export const IconTileOutline = ({ size = 40, stroke = "#1A1A1A", children }) => (
  <svg
    width={size}
    height={size * 1.36}
    viewBox="0 0 40 54"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect
      x="1"
      y="1"
      width="38"
      height="52"
      rx="5"
      stroke={stroke}
      strokeWidth="1.5"
      fill="#F5F0E8"
    />
    {children}
  </svg>
);

export const IconPlay = ({ size = 40 }) => (
  <IconTileOutline size={size}>
    <path d="M15 17L27 27L15 37V17Z" fill="#3D7A5A" />
  </IconTileOutline>
);

export const IconHint = ({ size = 40 }) => (
  <IconTileOutline size={size}>
    <path
      d="M20 14C15.6 14 12 17.4 12 21.5C12 24 13.2 26.2 15 27.7V32H25V27.7C26.8 26.2 28 24 28 21.5C28 17.4 24.4 14 20 14Z"
      fill="#3D7A5A"
    />
    <rect x="16" y="34" width="8" height="2" fill="#1A1A1A" />
    <rect x="17" y="38" width="6" height="2" fill="#1A1A1A" />
  </IconTileOutline>
);

export const IconAnalyze = ({ size = 40 }) => (
  <IconTileOutline size={size}>
    <rect x="10" y="34" width="5" height="10" fill="#3D7A5A" />
    <rect x="17.5" y="28" width="5" height="16" fill="#3D7A5A" />
    <rect x="25" y="20" width="5" height="24" fill="#C0392B" />
    <path d="M10 18L30 10" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" />
  </IconTileOutline>
);

export const IconCourse = ({ size = 40 }) => (
  <IconTileOutline size={size}>
    <rect x="10" y="16" width="20" height="3" fill="#1A1A1A" />
    <rect x="10" y="23" width="20" height="3" fill="#3D7A5A" />
    <rect x="10" y="30" width="14" height="3" fill="#1A1A1A" />
    <rect x="10" y="37" width="16" height="3" fill="#3D7A5A" />
  </IconTileOutline>
);

export const IconCommunity = ({ size = 40 }) => (
  <IconTileOutline size={size}>
    <circle cx="14" cy="22" r="4" fill="#3D7A5A" />
    <circle cx="26" cy="22" r="4" fill="#C0392B" />
    <circle cx="20" cy="32" r="5" fill="#1A1A1A" />
  </IconTileOutline>
);

// Horizontal tile strip divider (pure decorative)
export const TileStripDivider = ({ className = "" }) => (
  <svg
    viewBox="0 0 600 24"
    width="100%"
    height="24"
    preserveAspectRatio="none"
    aria-hidden="true"
    className={className}
  >
    {Array.from({ length: 20 }).map((_, i) => (
      <rect
        key={i}
        x={i * 31 + 4}
        y="4"
        width="24"
        height="16"
        rx="3"
        fill="none"
        stroke="#3D7A5A"
        strokeOpacity={i % 5 === 0 ? 0.9 : 0.25}
        strokeWidth="1"
      />
    ))}
  </svg>
);
